import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { defaultRoute, features, type PageKey, routes } from '@/shared/config';
import type { IconName } from '@/shared/ui';
import { FullPageLoader } from '@/shared/ui';
import { DashboardLayout } from '../layouts/dashboard-layout';
import { SimpleLayout } from '../layouts/simple-layout';
import { AdminRoute, BotRoute, GuestRoute, ProtectedRoute } from './guards';
import { RouteError } from './route-error';

/** Screens that are not built yet land on the placeholder (clearly marked as temporary). */
const comingSoon = () => import('@/pages/status/coming-soon').then((m) => m.ComingSoonPage);

const notFound = () => import('@/pages/status/not-found').then((m) => m.NotFoundPage);

/**
 * One page route: the path, the `handle` the header and tabs read, and the lazily loaded
 * component (the placeholder while a screen is still pending).
 */
function page(
  path: string,
  key: PageKey,
  icon: IconName,
  load: () => Promise<ComponentType> = comingSoon,
): RouteObject {
  return {
    path,
    handle: { page: key, icon },
    lazy: async () => ({ Component: await load() }),
  };
}

// Marketing analytics is behind a config flag: the endpoints exist, the screens are commented
// out in the production client (VITE_FEATURE_MARKETING_ANALYTICS).
const marketingRoutes: RouteObject[] = features.marketingAnalytics
  ? [
      { path: '/analytics', element: <Navigate to={routes.analyticsCalls} replace /> },
      page(routes.analyticsCalls, 'analyticsCalls', 'phone', () =>
        import('@/pages/analytics/calls').then((m) => m.AnalyticsCallsPage),
      ),
      page(routes.analyticsAppointmentTracking, 'analyticsAppointmentTracking', 'funnel', () =>
        import('@/pages/analytics/appointment-tracking').then(
          (m) => m.AnalyticsAppointmentTrackingPage,
        ),
      ),
      page(routes.analyticsLocationSite, 'analyticsLocationSite', 'globe', () =>
        import('@/pages/analytics/location-site').then((m) => m.AnalyticsLocationSitePage),
      ),
      page(routes.analyticsReviews, 'analyticsReviews', 'star', () =>
        import('@/pages/analytics/reviews').then((m) => m.AnalyticsReviewsPage),
      ),
    ]
  : [];

const routeTree: RouteObject[] = [
  {
    errorElement: <RouteError />,
    hydrateFallbackElement: <FullPageLoader />,
    children: [
      {
        element: <GuestRoute />,
        children: [
          {
            path: routes.login,
            lazy: async () => ({
              Component: (await import('@/pages/login')).LoginPage,
            }),
          },
        ],
      },
      {
        element: <SimpleLayout />,
        children: [
          {
            path: routes.forbidden,
            lazy: async () => ({
              Component: (await import('@/pages/status/forbidden')).ForbiddenPage,
            }),
          },
          { path: routes.notFound, lazy: async () => ({ Component: await notFound() }) },
          {
            path: routes.serverError,
            lazy: async () => ({
              Component: (await import('@/pages/status/server-error')).ServerErrorPage,
            }),
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { index: true, element: <Navigate to={defaultRoute} replace /> },
              page(routes.practiceDashboard, 'practiceDashboard', 'chart', () =>
                import('@/pages/practice-dashboard').then((m) => m.PracticeDashboardPage),
              ),
              page(routes.practiceReport, 'practiceReport', 'receipt', () =>
                import('@/pages/practice-report').then((m) => m.PracticeReportPage),
              ),
              page(routes.appointmentStatistics, 'appointmentStatistics', 'calendar', () =>
                import('@/pages/appointment-statistics').then((m) => m.AppointmentStatisticsPage),
              ),
              page(routes.newPatientStatistics, 'newPatientStatistics', 'usersplus', () =>
                import('@/pages/new-patient-statistics').then((m) => m.NewPatientStatisticsPage),
              ),
              page(routes.revenueAnalysis, 'revenueAnalysis', 'receipt', () =>
                import('@/pages/revenue-analysis').then((m) => m.RevenueAnalysisPage),
              ),
              page(routes.patientMap, 'patientMap', 'map', () =>
                import('@/pages/patient-map').then((m) => m.PatientMapPage),
              ),
              page(routes.versionHistory, 'versionHistory', 'history', () =>
                import('@/pages/version-history').then((m) => m.VersionHistoryPage),
              ),
              page(routes.loginHistory, 'loginHistory', 'history', () =>
                import('@/pages/login-history').then((m) => m.LoginHistoryPage),
              ),
              page(routes.profile, 'profile', 'user', () =>
                import('@/pages/profile').then((m) => m.ProfilePage),
              ),
              page(routes.tickets, 'tickets', 'ticket', () =>
                import('@/pages/tickets').then((m) => m.TicketsPage),
              ),
              page(routes.ticketCreate, 'ticketCreate', 'ticket', () =>
                import('@/pages/ticket-create').then((m) => m.TicketCreatePage),
              ),
              page(routes.ticketDetail, 'ticketDetail', 'ticket', () =>
                import('@/pages/ticket-detail').then((m) => m.TicketDetailPage),
              ),
              page(routes.whatsappLogs, 'whatsappLogs', 'message', () =>
                import('@/pages/whatsapp-logs').then((m) => m.WhatsAppLogsPage),
              ),
              {
                element: <BotRoute />,
                children: [
                  page(routes.botConfiguration, 'botConfiguration', 'bot', () =>
                    import('@/pages/bot-configuration').then((m) => m.BotConfigurationPage),
                  ),
                ],
              },
              {
                element: <AdminRoute />,
                children: [
                  page(routes.users, 'users', 'user', () =>
                    import('@/pages/users').then((m) => m.UsersPage),
                  ),
                  page(routes.userAdd, 'userAdd', 'usersplus', () =>
                    import('@/pages/user-add').then((m) => m.UserAddPage),
                  ),
                  page(routes.userEdit, 'userEdit', 'pencil', () =>
                    import('@/pages/user-edit').then((m) => m.UserEditPage),
                  ),
                  page(routes.userLoginHistory, 'loginHistory', 'history', () =>
                    import('@/pages/login-history').then((m) => m.LoginHistoryPage),
                  ),
                ],
              },
              ...marketingRoutes,
              { path: '*', lazy: async () => ({ Component: await notFound() }) },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routeTree);
